import { Box, Typography } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import { ChatMessage } from "../types/ginoQuery";

const isArabic = (text: string) => /[\u0600-\u06FF]/.test(text);

const MessageBubble = ({ message }: { message: ChatMessage }) => {
  const isUser = message.role === "user";
  const rtl = isArabic(message.text);

  return (
    <Box sx={{ ...styles.container, flexDirection: isUser ? "row-reverse" : "row", ...(rtl && !isUser ? { flexDirection: "row-reverse" } : {}) }}>
      <Box sx={{ ...styles.avatar, ...(isUser ? styles.userAvatar : styles.ginoAvatar) }}>
        {isUser ? <Typography sx={styles.avatarText}>You</Typography> : <StarIcon sx={{ fontSize: 16, color: "#fff" }} />}
      </Box>
      <Box>
        {message.step && message.step.total > 1 && (
          <Box sx={styles.stepBadge}>
            <Box sx={styles.stepDots}>
              {Array.from({ length: message.step.total }).map((_, i) => (
                <Box key={i} sx={{ ...styles.stepDot, ...(i < message.step!.current ? styles.stepDotActive : {}) }} />
              ))}
            </Box>
            <Typography component="span" sx={styles.stepText}>
              Step {message.step.current} of {message.step.total}
            </Typography>
          </Box>
        )}
        {message.isMarkdown ? (
          <Box
            sx={{ ...styles.bubble, ...(isUser ? styles.userBubble : styles.ginoBubble) }}
            {...(rtl ? { dir: "rtl" } : {})}
            dangerouslySetInnerHTML={{ __html: message.text }}
          />
        ) : (
          <Box
            sx={{ ...styles.bubble, ...(isUser ? styles.userBubble : styles.ginoBubble) }}
            {...(rtl ? { dir: "rtl" } : {})}
          >
            {message.text}
          </Box>
        )}
        <Typography sx={{ ...styles.time, ...(isUser ? { textAlign: "right" } : {}) }}>{message.time}</Typography>
      </Box>
    </Box>
  );
};

export default MessageBubble;

const styles = {
  container: { display: "flex", gap: "10px", mb: "18px", alignItems: "flex-start", animation: "ginoSlideUp 0.32s cubic-bezier(0.16,1,0.3,1) forwards", "@keyframes ginoSlideUp": { from: { opacity: 0, transform: "translateY(12px)" }, to: { opacity: 1, transform: "translateY(0)" } } },
  avatar: { width: 32, height: 32, borderRadius: "11px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, mt: "2px", boxShadow: "0 2px 6px -1px rgba(16,24,40,0.16)" },
  ginoAvatar: { background: "linear-gradient(135deg, #159c66 0%, #106048 55%, #0c4d39 100%)", color: "#fff" },
  userAvatar: { background: "#eef1f4", color: "#475467" },
  avatarText: { fontSize: 13, fontWeight: 700, fontFamily: "Montserrat" },
  bubble: { maxWidth: "85%", borderRadius: "18px", padding: "12px 16px", lineHeight: 1.65, fontSize: 14, fontFamily: "Montserrat", "& p": { mb: 1, "&:last-child": { mb: 0 } }, "& ul, & ol": { m: "6px 0 6px 18px" }, "& li": { mb: "4px" }, "& strong": { fontWeight: 700 }, "& em": { color: "inherit", opacity: 0.85 }, "& a": { color: "#106048", fontWeight: 600 }, "& code": { background: "rgba(0,0,0,0.06)", padding: "1px 5px", borderRadius: "4px", fontSize: 13 } },
  ginoBubble: { background: "#fff", color: "#111827", border: "1px solid #eef0f2", borderTopLeftRadius: "6px", boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 6px 18px -12px rgba(16,24,40,0.12)" },
  userBubble: { background: "linear-gradient(135deg, #12855f 0%, #106048 100%)", color: "#fff", borderTopRightRadius: "6px", boxShadow: "0 4px 12px -4px rgba(16,96,72,0.4)" },
  time: { fontSize: 11, color: "#9ca3af", mt: "5px", fontFamily: "Montserrat" },
  stepBadge: { display: "inline-flex", alignItems: "center", gap: "7px", mb: "6px", ml: "2px" },
  stepDots: { display: "flex", gap: "3px" },
  stepDot: { width: 5, height: 5, borderRadius: "50%", background: "#e2e6ea", transition: "background 0.2s" },
  stepDotActive: { background: "linear-gradient(135deg, #159c66 0%, #106048 100%)" },
  stepText: { fontSize: 11, fontWeight: 600, color: "#8a9199", fontFamily: "Montserrat", letterSpacing: "0.02em" },
};
